import axios from 'axios';
import { API_BASE_URL } from './config';

/**
 * Service für die Verwaltung von Webserver (Plesk) Bestellungen
 */
class WebserverService {
    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true
        }); 
    }

    /**
     * Bestellt einen neuen Webserver
     * @param {Object} orderDetails - Details der Bestellung
     * @param {string} orderDetails.storage - Speichergröße (z.B. '10gb', '50gb')
     * @param {number} orderDetails.domains - Anzahl der Domains
     * @param {Array} orderDetails.domainNames - Liste der Domainnamen
     * @param {number} orderDetails.mailboxes - Anzahl der E-Mail-Postfächer
     * @param {number} orderDetails.databases - Anzahl der Datenbanken
     * @param {number} orderDetails.totalPrice - Gesamtpreis der Bestellung
     * @param {string} orderDetails.serviceName - Optional: Name des Webservers
     * @returns {Promise} - Promise mit dem Ergebnis der Bestellung
     */
    orderWebserver(orderDetails) {
        // Generiere einen Standard-Servicenamen, falls keiner angegeben wurde
        const serviceName = orderDetails.serviceName || `Webserver mit ${orderDetails.domains} Domain(s)`;
        
        return this.axiosInstance.post('/iaas-service/order', {
            serviceType: 'webserver',
            serviceName: serviceName,
            configuration: {
                storage: orderDetails.storage,
                domains: orderDetails.domains,
                domainNames: orderDetails.domainNames || [],
                mailboxes: orderDetails.mailboxes || 0,
                databases: orderDetails.databases || 0
            },
            totalPrice: orderDetails.totalPrice,
            ...(orderDetails.endCustomerPrice ? { endCustomerPrice: parseFloat(orderDetails.endCustomerPrice) } : {})
        });
    }

    /**
     * Holt alle Webserver eines Benutzers
     * @returns {Promise} - Promise mit den Webservern des Benutzers
     */
    getUserWebservers() {
        return this.axiosInstance.get('/iaas-service', {
            params: { serviceType: 'webserver' }
        });
    }

    /**
     * Holt alle aktiven Webserver eines Benutzers
     * @returns {Promise} - Promise mit den aktiven Webservern des Benutzers
     */
    getActiveWebservers() {
        return this.axiosInstance.get('/iaas-service/active', {
            params: { serviceType: 'webserver' }
        });
    }
    
    /**
     * Fügt Domains zu einem bestehenden Webserver hinzu
     * @param {Object} orderDetails - Details der Bestellung
     * @param {string} orderDetails.serviceName - Name des Webservers, zu dem Domains hinzugefügt werden sollen
     * @param {number} [orderDetails.webserverId] - Optional: ID des Webservers (für Kompatibilität mit älteren Implementierungen)
     * @param {number} orderDetails.domains - Anzahl der Domains
     * @param {Array} orderDetails.domainNames - Liste der Domainnamen
     * @param {number} orderDetails.totalPrice - Gesamtpreis der Bestellung
     * @returns {Promise} - Promise mit dem Ergebnis der Bestellung
     */
    addDomainsToWebserver(orderDetails) {
        const payload = {
            domains: orderDetails.domains,
            domainNames: orderDetails.domainNames || [],
            totalPrice: orderDetails.totalPrice
        };
        
        // Füge serviceName hinzu, wenn vorhanden
        if (orderDetails.serviceName) {
            payload.serviceName = orderDetails.serviceName;
        }
        
        // Füge webserverId hinzu, wenn vorhanden (für Kompatibilität)
        if (orderDetails.webserverId) {
            payload.webserverId = orderDetails.webserverId;
        }
        
        return this.axiosInstance.post('/iaas-service/webserver/add-domains', payload);
    }
}

export default new WebserverService();